/**
 * Раздел «Поездки» кабинета агентства.
 *
 * Главное правило экрана: то, где ход за агентством, идёт первым, выделено и посчитано — и в
 * плашке сверху, и в фильтре. До этого поездка с открытым шагом ничем не отличалась от
 * завершённой полугодовой давности, и агенту приходилось открывать карточки по одной.
 *
 * Статус, «что дальше», порядок и счётчики считает `tripView` — здесь только раскладка.
 */
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { api, type CabinetTrip } from '../../../src/api/client'
import { CabinetShell } from '../../../src/features/cabinet/CabinetShell'
import {
  countTrips,
  filterTrips,
  sortTrips,
  tripStatus,
  type TripFilter,
} from '../../../src/features/cabinet/tripView'
import { plural, useApp } from '../../../src/i18n'
import { fmtDateRange, fmtDayTile } from '../../../src/lib/format'
import { tone } from '../../../src/theme/tones'
import { Button, Card, Icon, PressCard, QueryState, Seg, SkeletonLines, Tag } from '../../../src/ui'

const FILTERS: TripFilter[] = ['all', 'action', 'wait', 'done']

function TripRow({ row, onPress }: { row: CabinetTrip; onPress: () => void }) {
  const { l, lang } = useApp()
  const st = tripStatus(row)
  const tile = fmtDayTile(row.dateFrom, lang)
  const act = st.kind === 'action'
  const sub = [
    fmtDateRange(row.dateFrom, row.dateTo, lang),
    row.travellers.length ? plural(l('cab.trips.people'), row.travellers.length, lang) : '',
    `№ ${row.number}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <PressCard
      onPress={onPress}
      padding={14}
      borderColor={act ? tone(st.tone).solid : '$borderColor'}
      borderWidth={act ? 2 : 1}
      opacity={st.kind === 'done' ? 0.85 : 1}
    >
      <XStack gap={14} alignItems="center" flexWrap="wrap">
        {/* Плитка вылета: по ней список читается как расписание, а не как таблица номеров. */}
        {tile && (
          <YStack
            width={56}
            paddingVertical={8}
            borderRadius={10}
            backgroundColor="$bgLayout"
            alignItems="center"
            flexShrink={0}
          >
            <Text fontSize={20} fontWeight="800" color="$color" lineHeight={22}>
              {tile.day}
            </Text>
            <Text fontSize={12} color="$muted">
              {tile.month}
            </Text>
          </YStack>
        )}
        <YStack flex={1} minWidth={180} gap={4}>
          <Text fontSize={16} fontWeight="700" color="$color">
            {[row.originCityName, row.destinationCityName].filter(Boolean).join(' → ') ||
              `№ ${row.number}`}
          </Text>
          <Text fontSize={13} color="$muted">
            {sub}
          </Text>
        </YStack>
        <YStack flexShrink={0} gap={6} alignItems="flex-start" minWidth={160}>
          <Tag tone={st.tone}>{l(st.pill)}</Tag>
          {st.next && (
            <Text fontSize={13} color={act ? tone(st.tone).text : '$muted'} fontWeight={act ? '600' : '400'}>
              {l(st.next)}
            </Text>
          )}
          {/* Открытое уточнение — не статус, а примечание: ход может быть и за агентством. */}
          {row.awaitingAnswer && (
            <Text fontSize={12} color={tone('gold').text}>
              {l('cab.trips.thread.pending')}
            </Text>
          )}
        </YStack>
        <Icon name="chevron-right" size={20} color="$tertiary" />
      </XStack>
    </PressCard>
  )
}

export default function CabinetTripsScreen() {
  const { l, lang } = useApp()
  const router = useRouter()
  const [filter, setFilter] = useState<TripFilter>('all')
  const q = useQuery({ queryKey: ['cabinet', 'trips'], queryFn: api.cabinetTrips })

  const rows = q.data ?? []
  const counts = countTrips(rows)
  const shown = sortTrips(filterTrips(rows, filter))

  return (
    <CabinetShell section="trips">
      <YStack gap={6}>
        <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4}>
          {l('cab.nav.trips')}
        </Text>
        <Text fontSize={14} color="$muted">
          {l('cab.trips.subtitle')}
        </Text>
      </YStack>

      {/* Плашка — короткий путь к своим задачам; под включённым фильтром она избыточна. */}
      {counts.action > 0 && filter !== 'action' && (
        <XStack
          alignItems="center"
          gap={12}
          flexWrap="wrap"
          padding={14}
          borderRadius={10}
          backgroundColor={tone('gold').soft}
        >
          <Text fontSize={15} fontWeight="700" color={tone('gold').text} flex={1} minWidth={200}>
            {plural(l('cab.trips.banner'), counts.action, lang)}
          </Text>
          <Button height={44} onPress={() => setFilter('action')}>
            {l('cab.trips.bannerShow')}
          </Button>
        </XStack>
      )}

      <XStack
        role="tablist"
        aria-label={l('cab.filter.label')}
        gap={4}
        padding={4}
        borderRadius={7}
        backgroundColor="$bgLayout"
        flexWrap="wrap"
      >
        {FILTERS.map((f) => (
          <Seg
            key={f}
            active={filter === f}
            label={l(`cab.filter.${f}`)}
            badge={counts[f]}
            badgeTone="muted"
            onPress={() => setFilter(f)}
            minHeight={44}
          />
        ))}
      </XStack>

      <QueryState q={q}>
        {q.isLoading ? (
          <SkeletonLines lines={4} />
        ) : shown.length === 0 ? (
          <Card alignItems="center" gap={10} padding={32} borderStyle="dashed">
            <Stack
              width={44}
              height={44}
              borderRadius={12}
              backgroundColor="$primaryBg"
              alignItems="center"
              justifyContent="center"
            >
              <Icon name="plane" size={22} color="$primary" />
            </Stack>
            <Text fontSize={14} color="$muted" textAlign="center" maxWidth={440}>
              {l(`cab.trips.empty.${filter}`)}
            </Text>
            {filter !== 'all' && (
              <Button height={44} variant="default" onPress={() => setFilter('all')}>
                {l('cab.filter.reset')}
              </Button>
            )}
          </Card>
        ) : (
          <YStack gap={10}>
            {shown.map((row) => (
              <TripRow
                key={row.id}
                row={row}
                onPress={() => router.push(`/cabinet/trips/${row.id}`)}
              />
            ))}
          </YStack>
        )}
      </QueryState>
    </CabinetShell>
  )
}
